import { atom, WritableAtom } from 'jotai';
import { selectedStepAtom, workflowAtom } from './atoms';
import { ServerStep, ServerStepGroup } from './ServerWorkflowType';

/**
 * Utility function to update a step within the workflow tree
 */
const updateStepInTree = (
  stepGroup: ServerStepGroup | null,
  stepId: number,
  updateFunction: (step: ServerStep) => ServerStep
): ServerStepGroup | null => {
  if (!stepGroup) {
    return null;
  }

  const updatedSteps = stepGroup.steps?.map((step) => {
    if (step.workflowStepId === stepId) {
      return updateFunction(step);
    }

    return {
      ...step,
      failbackStepGroup: updateStepInTree(
        step.failbackStepGroup || null,
        stepId,
        updateFunction
      ),
      nestedStepGroup: updateStepInTree(
        step.nestedStepGroup || null,
        stepId,
        updateFunction
      ),
    };
  });

  return {
    ...stepGroup,
    nextStepGroup: updateStepInTree(
      stepGroup.nextStepGroup || null,
      stepId,
      updateFunction
    ),
    steps: updatedSteps,
  };
};

// Step property atoms
export const stepTitleAtom = atom(
  (get) => get(selectedStepAtom)?.stepTitle || '',
  (get, set, newValue: string) => {
    const step = get(selectedStepAtom);
    if (!step?.workflowStepId) {
      return;
    }

    const workflow = get(workflowAtom);
    set(workflowAtom, {
      ...workflow,
      failbackStepGroup: updateStepInTree(
        workflow.failbackStepGroup,
        step.workflowStepId,
        (s) => ({ ...s, stepTitle: newValue })
      ),
      nextStepGroup: updateStepInTree(
        workflow.nextStepGroup,
        step.workflowStepId,
        (s) => ({ ...s, stepTitle: newValue })
      ),
    });
  }
);

export const stepIdAtom = atom(
  (get) => get(selectedStepAtom)?.stepId || '',
  (get, set, newValue: string) => {
    const step = get(selectedStepAtom);
    if (!step?.workflowStepId) {
      return;
    }

    const workflow = get(workflowAtom);
    set(workflowAtom, {
      ...workflow,
      failbackStepGroup: updateStepInTree(
        workflow.failbackStepGroup,
        step.workflowStepId,
        (s) => ({ ...s, stepId: newValue })
      ),
      nextStepGroup: updateStepInTree(
        workflow.nextStepGroup,
        step.workflowStepId,
        (s) => ({ ...s, stepId: newValue })
      ),
    });
  }
);

export const stepIndexAtom = atom(
  (get) => get(selectedStepAtom)?.stepIndex || '',
  (get, set, newValue: string) => {
    const step = get(selectedStepAtom);
    if (!step?.workflowStepId) {
      return;
    }

    const workflow = get(workflowAtom);
    set(workflowAtom, {
      ...workflow,
      failbackStepGroup: updateStepInTree(
        workflow.failbackStepGroup,
        step.workflowStepId,
        (s) => ({ ...s, stepIndex: newValue })
      ),
      nextStepGroup: updateStepInTree(
        workflow.nextStepGroup,
        step.workflowStepId,
        (s) => ({ ...s, stepIndex: newValue })
      ),
    });
  }
);

export const stepConditionAtom = atom(
  (get) => get(selectedStepAtom)?.stepCondition || '',
  (get, set, newValue: string) => {
    const step = get(selectedStepAtom);
    if (!step?.workflowStepId) {
      return;
    }

    const workflow = get(workflowAtom);
    set(workflowAtom, {
      ...workflow,
      failbackStepGroup: updateStepInTree(
        workflow.failbackStepGroup,
        step.workflowStepId,
        (s) => ({ ...s, stepCondition: newValue })
      ),
      nextStepGroup: updateStepInTree(
        workflow.nextStepGroup,
        step.workflowStepId,
        (s) => ({ ...s, stepCondition: newValue })
      ),
    });
  }
);

export const retryNumberAtom = atom(
  (get) => get(selectedStepAtom)?.retryNumber ?? 0,
  (get, set, newValue: number) => {
    const step = get(selectedStepAtom);
    if (!step?.workflowStepId) {
      return;
    }

    const workflow = get(workflowAtom);
    set(workflowAtom, {
      ...workflow,
      failbackStepGroup: updateStepInTree(
        workflow.failbackStepGroup,
        step.workflowStepId,
        (s) => ({ ...s, retryNumber: newValue })
      ),
      nextStepGroup: updateStepInTree(
        workflow.nextStepGroup,
        step.workflowStepId,
        (s) => ({ ...s, retryNumber: newValue })
      ),
    });
  }
);

export const retryDelayAtom = atom(
  (get) => get(selectedStepAtom)?.retryDelay ?? 0,
  (get, set, newValue: number) => {
    const step = get(selectedStepAtom);
    if (!step?.workflowStepId) {
      return;
    }

    const workflow = get(workflowAtom);
    set(workflowAtom, {
      ...workflow,
      failbackStepGroup: updateStepInTree(
        workflow.failbackStepGroup,
        step.workflowStepId,
        (s) => ({ ...s, retryDelay: newValue })
      ),
      nextStepGroup: updateStepInTree(
        workflow.nextStepGroup,
        step.workflowStepId,
        (s) => ({ ...s, retryDelay: newValue })
      ),
    });
  }
);

export const retryDelayMultiplierAtom = atom(
  (get) => get(selectedStepAtom)?.retryDelayMultiplier ?? 0,
  (get, set, newValue: number) => {
    const step = get(selectedStepAtom);
    if (!step?.workflowStepId) {
      return;
    }

    const workflow = get(workflowAtom);
    set(workflowAtom, {
      ...workflow,
      failbackStepGroup: updateStepInTree(
        workflow.failbackStepGroup,
        step.workflowStepId,
        (s) => ({ ...s, retryDelayMultiplier: newValue })
      ),
      nextStepGroup: updateStepInTree(
        workflow.nextStepGroup,
        step.workflowStepId,
        (s) => ({ ...s, retryDelayMultiplier: newValue })
      ),
    });
  }
);

/**
 * Creates an atom for a specific parameter value
 */
export const createParameterValueAtom = (
  parameterGroupIndex: number,
  parameterIndex: number
): WritableAtom<string, [string], void> => {
  return atom(
    (get) => {
      const step = get(selectedStepAtom);
      return (
        step?.paramGroups?.[parameterGroupIndex]?.parameters?.[parameterIndex]
          ?.value || ''
      );
    },
    (get, set, newValue: string) => {
      const step = get(selectedStepAtom);
      if (!step?.workflowStepId) {
        return;
      }

      const workflow = get(workflowAtom);
      set(workflowAtom, {
        ...workflow,
        failbackStepGroup: updateStepInTree(
          workflow.failbackStepGroup,
          step.workflowStepId,
          (s) => ({
            ...s,
            paramGroups: s.paramGroups.map((group, gIndex) =>
              gIndex === parameterGroupIndex
                ? {
                    ...group,
                    parameters: group.parameters.map((parameter, pIndex) =>
                      pIndex === parameterIndex
                        ? { ...parameter, value: newValue }
                        : parameter
                    ),
                  }
                : group
            ),
          })
        ),
        nextStepGroup: updateStepInTree(
          workflow.nextStepGroup,
          step.workflowStepId,
          (s) => ({
            ...s,
            paramGroups: s.paramGroups.map((group, gIndex) =>
              gIndex === parameterGroupIndex
                ? {
                    ...group,
                    parameters: group.parameters.map((parameter, pIndex) =>
                      pIndex === parameterIndex
                        ? { ...parameter, value: newValue }
                        : parameter
                    ),
                  }
                : group
            ),
          })
        ),
      });
    }
  );
};

/**
 * Creates an atom for a parameter's isDynamic flag
 */
export const createParameterIsDynamicAtom = (
  parameterGroupIndex: number,
  parameterIndex: number
): WritableAtom<boolean, [boolean], void> => {
  return atom(
    (get) => {
      const step = get(selectedStepAtom);
      return (
        step?.paramGroups?.[parameterGroupIndex]?.parameters?.[parameterIndex]
          ?.isDynamic || false
      );
    },
    (get, set, newValue: boolean) => {
      const step = get(selectedStepAtom);
      if (!step?.workflowStepId) {
        return;
      }

      const workflow = get(workflowAtom);
      set(workflowAtom, {
        ...workflow,
        failbackStepGroup: updateStepInTree(
          workflow.failbackStepGroup,
          step.workflowStepId,
          (s) => ({
            ...s,
            paramGroups: s.paramGroups.map((group, gIndex) =>
              gIndex === parameterGroupIndex
                ? {
                    ...group,
                    parameters: group.parameters.map((parameter, pIndex) =>
                      pIndex === parameterIndex
                        ? { ...parameter, isDynamic: newValue }
                        : parameter
                    ),
                  }
                : group
            ),
          })
        ),
        nextStepGroup: updateStepInTree(
          workflow.nextStepGroup,
          step.workflowStepId,
          (s) => ({
            ...s,
            paramGroups: s.paramGroups.map((group, gIndex) =>
              gIndex === parameterGroupIndex
                ? {
                    ...group,
                    parameters: group.parameters.map((parameter, pIndex) =>
                      pIndex === parameterIndex
                        ? { ...parameter, isDynamic: newValue }
                        : parameter
                    ),
                  }
                : group
            ),
          })
        ),
      });
    }
  );
};

/**
 * Creates an atom for a parameter's escSpecSymbols flag
 */
export const createParameterEscSpecSymbolsAtom = (
  parameterGroupIndex: number,
  parameterIndex: number
): WritableAtom<boolean, [boolean], void> => {
  return atom(
    (get) => {
      const step = get(selectedStepAtom);
      return (
        step?.paramGroups?.[parameterGroupIndex]?.parameters?.[parameterIndex]
          ?.escSpecSymbols || false
      );
    },
    (get, set, newValue: boolean) => {
      const step = get(selectedStepAtom);
      if (!step?.workflowStepId) {
        return;
      }

      const workflow = get(workflowAtom);
      set(workflowAtom, {
        ...workflow,
        failbackStepGroup: updateStepInTree(
          workflow.failbackStepGroup,
          step.workflowStepId,
          (s) => ({
            ...s,
            paramGroups: s.paramGroups.map((group, gIndex) =>
              gIndex === parameterGroupIndex
                ? {
                    ...group,
                    parameters: group.parameters.map((parameter, pIndex) =>
                      pIndex === parameterIndex
                        ? { ...parameter, escSpecSymbols: newValue }
                        : parameter
                    ),
                  }
                : group
            ),
          })
        ),
        nextStepGroup: updateStepInTree(
          workflow.nextStepGroup,
          step.workflowStepId,
          (s) => ({
            ...s,
            paramGroups: s.paramGroups.map((group, gIndex) =>
              gIndex === parameterGroupIndex
                ? {
                    ...group,
                    parameters: group.parameters.map((parameter, pIndex) =>
                      pIndex === parameterIndex
                        ? { ...parameter, escSpecSymbols: newValue }
                        : parameter
                    ),
                  }
                : group
            ),
          })
        ),
      });
    }
  );
};
